"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AGENT_SUGGESTED_REPLIES_ENABLED = exports.AGENT_ENCODED_ANSWER_MAX_CHARS = exports.AGENT_MESSAGE_MAX_CHARS = void 0;
exports.isEncodedAgentAnswer = isEncodedAgentAnswer;
exports.agentMessageProseLength = agentMessageProseLength;
exports.checkAgentMessageLength = checkAgentMessageLength;
/**
 * Max characters allowed in a single **human-typed** agent chat message — the prose a user
 * types (or pastes) into the composer. Shared so the web can guard the chat input and surface a
 * clear, actionable message when a user pastes something oversized (e.g. dumping a whole document
 * or a large skill into the chat), instead of letting the send fail with an opaque 400 (ENG-3779).
 * Keep this in sync with the `content` Zod cap on the messages POST route.
 */
exports.AGENT_MESSAGE_MAX_CHARS = 10000;
/**
 * Max characters allowed in an **app-encoded** message — a `<ANSWER…>` tool answer or an
 * `<ATTACHMENTS…>` payload the UI assembles on the user's behalf (never typed prose). These are
 * machine-generated and their size scales with the user's *selection* (e.g. picking many creative
 * assets, or approving a multi-ad launch), so they legitimately run past the human-prose cap.
 *
 * They get their own, far larger ceiling because the prose cap leaked onto this path: a large
 * selection was hard-blocked with "Your message is too long … Please shorten it" — advice the user
 * cannot act on, since they never wrote the characters (regression of ENG-3779's intent). The
 * ceiling still bounds context growth / abuse. ~100k chars ≈ 1,600 asset references.
 */
exports.AGENT_ENCODED_ANSWER_MAX_CHARS = 100000;
/**
 * Wire markers the backend orchestrator parses out of a user message. A message that begins with
 * one of these is an app-assembled payload, not human prose. `submitAnswer` always emits
 * `<ANSWER…>` first (attachments, if any, are appended after), and attachment-only sends begin
 * with `<ATTACHMENTS…>`.
 */
const ENCODED_AGENT_PAYLOAD_RE = /^<(?:ANSWER|ATTACHMENTS)\b/;
/** True when `content` is an app-encoded tool-answer/attachment payload rather than typed prose. */
function isEncodedAgentAnswer(content) {
    return ENCODED_AGENT_PAYLOAD_RE.test(content.trimStart());
}
/**
 * Start of the app-assembled encoded block **anywhere** in a message: at the very start (a tool
 * answer or an attachment-only send) *or* on its own line after typed prose. The composer appends
 * `<ATTACHMENTS…>` below the user's prose when they type a message *and* attach products/files, so
 * unlike {@link ENCODED_AGENT_PAYLOAD_RE} (start-anchored) this also finds a block that follows
 * prose — the case where a short typed message plus attachments assembles into a payload whose
 * machine-generated attachment descriptions dwarf the prose. Anchored at a line start so a literal
 * `<ATTACHMENTS>` mid-sentence in prose isn't mistaken for the block.
 */
const ENCODED_BLOCK_ANYWHERE_RE = /(?:^|\n)<(?:ANSWER|ATTACHMENTS)\b/;
/**
 * Length of the human-typed prose portion of `content` — everything before the first app-assembled
 * `<ANSWER…>`/`<ATTACHMENTS…>` block (the whole message when there is none). This is what the
 * human-prose cap (`AGENT_MESSAGE_MAX_CHARS`) governs; the block itself is machine-generated and
 * scales with the user's *selection*, so it is excluded from the prose count.
 */
function agentMessageProseLength(content) {
    const match = ENCODED_BLOCK_ANYWHERE_RE.exec(content);
    const proseEnd = match ? match.index : content.length;
    return content.slice(0, proseEnd).trim().length;
}
/**
 * The per-message length policy for `content`, shared by the web composer/store backstop and the
 * messages/MCP route validation. Two independent ceilings:
 *
 * - the **human-typed prose** ({@link agentMessageProseLength}) is held to `AGENT_MESSAGE_MAX_CHARS`,
 *   so pasting a whole document is still stopped with actionable guidance (ENG-3779);
 * - the **whole assembled payload** (prose + any app-appended `<ANSWER…>`/`<ATTACHMENTS…>` block) is
 *   held to the far larger `AGENT_ENCODED_ANSWER_MAX_CHARS`.
 *
 * Separating the two is what lets a *short* typed message with several attached products/files
 * through: the attachments' machine-generated descriptions inflate the payload well past the prose
 * cap, but the user never typed those characters, so counting the whole payload against the prose
 * cap wrongly rejected the send with "your message is too long" (the reported bug).
 */
function checkAgentMessageLength(content) {
    const totalLength = content.trim().length;
    const proseLength = agentMessageProseLength(content);
    if (totalLength > exports.AGENT_ENCODED_ANSWER_MAX_CHARS) {
        return { ok: false, exceeded: 'total', proseLength, totalLength };
    }
    if (proseLength > exports.AGENT_MESSAGE_MAX_CHARS) {
        return { ok: false, exceeded: 'prose', proseLength, totalLength };
    }
    return { ok: true, exceeded: null, proseLength, totalLength };
}
/**
 * Global kill switch for contextual replies above the agent chat composer.
 *
 * Keep the web query, endpoint, renderer, and generation service connected behind this value so
 * setting it to `true` restores the existing end-to-end feature without additional code changes.
 */
exports.AGENT_SUGGESTED_REPLIES_ENABLED = false;
